import React, { useEffect, useState, forwardRef } from "react";
import { Form, Input, Select, Row, Col, Button, Switch } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import endpoints from "../../constants/endpoints";
import requests from "../../utilities/api";

// Every CMS page is offered as a link target, grouped by its page group.
const GROUP_LABELS = {
  page: "Pages",
  blogs: "Blogs",
  system: "System Pages",
  header: "Header",
  footer: "Footer",
};
const GROUP_ORDER = ["page", "blogs", "system", "header", "footer"];

const toPagePath = (group, slug) => {
  if (group === "page" && slug === "home") return "/";
  if (group === "blogs") return `/blog/${slug}`;
  if (group === "services") {
    if (slug.startsWith("categories/")) {
      return `/categories/${slug.slice("categories/".length)}`;
    }
  }
  return `/${slug}`;
};

const normaliseGroup = (group) => {
  const g = String(group || "page").toLowerCase();
  if (g === "blog" || g === "blog_page") return "blogs";
  return g;
};

const fetchAllCmsPages = async () => {
  const api = requests();
  const pages = [];
  for (let page = 1, lastPage = 1; page <= lastPage && page <= 20; page += 1) {
    const response = await api.get(endpoints.get_cms_pages_list, { limit: 100, page }, true);
    const body = response?.data;
    const list = Array.isArray(body) ? body : body?.data || [];
    pages.push(...list);
    lastPage = Number(body?.last_page) || 1;
  }
  return pages;
};

// Grouped Select options built from all CMS pages returned by the API.
const buildLinkOptions = (pages) => {
  const byGroup = {};
  pages
    .filter((p) => p?.slug)
    .forEach((p) => {
      const group = normaliseGroup(p.group);
      (byGroup[group] = byGroup[group] || []).push(p);
    });

  const groupKeys = [
    ...GROUP_ORDER.filter((g) => byGroup[g]),
    ...Object.keys(byGroup).filter((g) => !GROUP_ORDER.includes(g)).sort(),
  ];

  return groupKeys.map((group) => ({
    label: GROUP_LABELS[group] || group.charAt(0).toUpperCase() + group.slice(1),
    options: byGroup[group]
      .sort((a, b) => (a.title || a.slug).localeCompare(b.title || b.slug))
      .map((p) => {
        const slug = String(p.slug).replace(/^\/+/, "");
        const path = toPagePath(group, slug);
        const status = String(p.status || "").toUpperCase();
        const suffix = status && status !== "ACTIVE" ? ` · ${status.charAt(0)}${status.slice(1).toLowerCase()}` : "";
        return { value: path, label: `${p.title || slug} (${path})${suffix}` };
      }),
  }));
};

// One shared request for every link field on screen; retried on next call if it failed.
let cmsPageOptionsPromise = null;
export const loadCmsPageOptions = () => {
  if (!cmsPageOptionsPromise) {
    cmsPageOptionsPromise = fetchAllCmsPages()
      .then(buildLinkOptions)
      .catch((e) => {
        console.error(e);
        cmsPageOptionsPromise = null;
        return buildLinkOptions([]);
      });
  }
  return cmsPageOptionsPromise;
};

// Drop the cached list so newly created/renamed pages appear (called when the editor opens).
export const refreshCmsPageOptions = () => {
  cmsPageOptionsPromise = null;
};

export const UrlInput = forwardRef(
  ({ value, onChange, id, onFocus, onBlur, placeholder = "e.g. /contact-us or https://..." }, ref) => {
    const isObject = value && typeof value === "object";
    const type = isObject
      ? value.type || "internal"
      : value && typeof value === "string" && value.startsWith("http")
      ? "external"
      : "internal";
    const urlValue = isObject ? value.url : value;

    const [cmsPages, setCmsPages] = useState([]);

    useEffect(() => {
      let active = true;
      loadCmsPageOptions().then((pages) => active && setCmsPages(pages));
      return () => {
        active = false;
      };
    }, []);

    const allInternalOptions = cmsPages.filter((g) => g.label !== "Services");
    const serviceOptions = cmsPages.find((g) => g.label === "Services")?.options || [];

    const handleTypeChange = (newType) => {
      if (isObject) {
        onChange?.({ ...value, type: newType, url: "" });
      } else {
        onChange?.("");
      }
    };

    const handleUrlChange = (newUrl) => {
      if (isObject) {
        onChange?.({ ...value, type, url: newUrl });
      } else {
        onChange?.(newUrl);
      }
    };

    return (
      <Row gutter={8} ref={ref}>
        <Col span={8}>
          <Select
            id={id ? `${id}_type` : undefined}
            onFocus={onFocus}
            onBlur={onBlur}
            value={type}
            onChange={handleTypeChange}
            getPopupContainer={() => document.body}
            options={[
              { label: "Internal", value: "internal" },
              { label: "External", value: "external" },
              { label: "Services", value: "services" },
            ]}
            style={{ width: "100%" }}
          />
        </Col>
        <Col span={16}>
          {type === "internal" ? (
            <Select
              id={id ? `${id}_internal` : undefined}
              onFocus={onFocus}
              onBlur={onBlur}
              showSearch
              placeholder="Select Page..."
              value={urlValue || undefined}
              onChange={handleUrlChange}
              getPopupContainer={() => document.body}
              allowClear
              options={allInternalOptions}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          ) : type === "services" ? (
            <Select
              id={id ? `${id}_services` : undefined}
              onFocus={onFocus}
              onBlur={onBlur}
              showSearch
              placeholder="Select Category..."
              value={urlValue || undefined}
              onChange={handleUrlChange}
              getPopupContainer={() => document.body}
              allowClear
              options={serviceOptions}
              style={{ width: "100%" }}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          ) : (
            <Input
              id={id ? `${id}_external` : undefined}
              onFocus={onFocus}
              onBlur={onBlur}
              value={urlValue || ""}
              onChange={(e) => handleUrlChange(e.target.value)}
              placeholder={placeholder}
            />
          )}
        </Col>
      </Row>
    );
  }
);

function LinkRow({
  listName,
  fieldName,
  restField,
  index,
  total,
  single,
  labelText,
  linkText,
  allowSecondary,
  cmsPages,
  serviceOptions,
  onRemove,
  onMoveUp,
  onMoveDown,
}) {
  const form = Form.useFormInstance();
  const linkType = Form.useWatch([listName, fieldName, "type"], form) || "internal";

  const allInternalOptions = cmsPages.filter((g) => g.label !== "Services");

  return (
    <Row gutter={[12, 12]} align="middle" style={{ marginBottom: index < total - 1 ? 12 : 0 }}>
      <Col xs={24} md={allowSecondary ? 8 : 9}>
        {index === 0 && (
          <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13, color: "#4b5563" }}>
            {labelText}
          </div>
        )}
        <Form.Item {...restField} name={[fieldName, "label"]} style={{ marginBottom: 0 }}>
          <Input placeholder="Link Label (e.g. Services)" />
        </Form.Item>
      </Col>

      <Col xs={24} md={allowSecondary ? 10 : 11}>
        {index === 0 && (
          <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13, color: "#4b5563" }}>
            {linkText}
          </div>
        )}
        <Row gutter={8}>
          <Col span={8}>
            <Form.Item {...restField} name={[fieldName, "type"]} initialValue="internal" style={{ marginBottom: 0 }}>
              <Select
                getPopupContainer={(triggerNode) => triggerNode?.parentNode || document.body}
                options={[
                  { label: "Internal", value: "internal" },
                  { label: "External", value: "external" },
                  { label: "Services", value: "services" },
                ]}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item {...restField} name={[fieldName, "url"]} style={{ marginBottom: 0 }}>
              {linkType === "internal" ? (
                <Select
                  showSearch
                  placeholder="Select Page..."
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode?.parentNode || document.body}
                  options={allInternalOptions}
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              ) : linkType === "services" ? (
                <Select
                  showSearch
                  placeholder="Select Category..."
                  allowClear
                  getPopupContainer={(triggerNode) => triggerNode?.parentNode || document.body}
                  options={serviceOptions}
                  style={{ width: "100%" }}
                  filterOption={(input, option) =>
                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                  }
                />
              ) : (
                <Input placeholder="https://example.com" />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Col>

      {allowSecondary && (
        <Col xs={12} md={2} style={{ textAlign: "center", paddingTop: index === 0 ? 20 : 0 }}>
          {index === 0 && (
            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 12, color: "#4b5563" }}>
              Secondary
            </div>
          )}
          <Form.Item {...restField} name={[fieldName, "is_secondary"]} valuePropName="checked" style={{ marginBottom: 0 }}>
            <Switch size="small" />
          </Form.Item>
        </Col>
      )}

      {!single && (
        <Col xs={12} md={allowSecondary ? 4 : 4} style={{ display: "flex", gap: 4, alignItems: "center", paddingTop: index === 0 ? 20 : 0 }}>
          <Button
            size="small"
            disabled={index === 0}
            onClick={onMoveUp}
            style={{ width: 26, height: 26, padding: 0 }}
          >
            ↑
          </Button>
          <Button
            size="small"
            disabled={index === total - 1}
            onClick={onMoveDown}
            style={{ width: 26, height: 26, padding: 0 }}
          >
            ↓
          </Button>
          <Button
            size="small"
            danger
            type="text"
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        </Col>
      )}
    </Row>
  );
}

const LinkPicker = ({
  name,
  value,
  onChange,
  single = false,
  labelText = "Link Label",
  linkText = "Link Destination",
  allowSecondary = false,
  placeholder = "e.g. /contact-us or https://...",
}) => {
  const [cmsPages, setCmsPages] = useState([]);

  useEffect(() => {
    let active = true;
    loadCmsPageOptions().then((pages) => active && setCmsPages(pages));
    return () => {
      active = false;
    };
  }, []);

  const serviceOptions = cmsPages.find((g) => g.label === "Services")?.options || [];

  // If used as a direct input component without Form.List name prop:
  if (!name) {
    return <UrlInput value={value} onChange={onChange} placeholder={placeholder} />;
  }

  return (
    <Form.List name={name} initialValue={[{ type: "internal", label: "", url: "" }]}>
      {(fields, { add, remove, move }) => (
        <div style={{ background: "#fafafa", padding: 16, borderRadius: 8, border: "1px solid #f0f0f0" }}>
          {fields.map(({ key, name: fieldName, ...restField }, index) => (
            <LinkRow
              key={key}
              listName={name}
              fieldName={fieldName}
              restField={restField}
              index={index}
              total={fields.length}
              single={single}
              labelText={labelText}
              linkText={linkText}
              allowSecondary={allowSecondary}
              cmsPages={cmsPages}
              serviceOptions={serviceOptions}
              onRemove={() => remove(fieldName)}
              onMoveUp={() => move(index, index - 1)}
              onMoveDown={() => move(index, index + 1)}
            />
          ))}

          {(!single || fields.length < 1) && (
            <Button
              type="dashed"
              onClick={() => add({ type: "internal", label: "", url: "" })}
              block
              icon={<PlusOutlined />}
              style={{ marginTop: 12 }}
            >
              Add Link
            </Button>
          )}
        </div>
      )}
    </Form.List>
  );
};

export default LinkPicker;
